import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, CalendarRange, Pencil } from "lucide-react";
import { eur, formatDate } from "@/lib/format";
import { Field } from "@/pages/admin/AgentsTab";

export function CampaignsTab() {
  const [periods, setPeriods] = useState(null);
  const [selected, setSelected] = useState(null);
  const [promos, setPromos] = useState([]);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [pForm, setPForm] = useState({ name: "", start_date: "", end_date: "" });
  const [promoOpen, setPromoOpen] = useState(false);
  const [prForm, setPrForm] = useState({ name: "", prize: "", target: "", value_per_sale: "", type: "vendite" });
  const [editPeriod, setEditPeriod] = useState(null);
  const [epForm, setEpForm] = useState({ name: "", start_date: "", end_date: "" });
  const [editPromo, setEditPromo] = useState(null);
  const [eprForm, setEprForm] = useState({ name: "", prize: "", target: "", value_per_sale: "", type: "vendite" });

  const loadPeriods = useCallback(() => {
    api.get("/admin/periods").then((r) => {
      setPeriods(r.data);
      setSelected((cur) => cur || (r.data[0]?.id ?? null));
    }).catch(() => setPeriods([]));
  }, []);
  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const loadPromos = useCallback((pid) => {
    if (!pid) return setPromos([]);
    api.get(`/admin/periods/${pid}/promotions`).then((r) => setPromos(r.data)).catch(() => setPromos([]));
  }, []);
  useEffect(() => { loadPromos(selected); }, [selected, loadPromos]);

  const createPeriod = async () => {
    try { await api.post("/admin/periods", pForm); toast.success("Campagna creata"); setPeriodOpen(false); setPForm({ name: "", start_date: "", end_date: "" }); loadPeriods(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const activate = async (id) => {
    try { await api.post(`/admin/periods/${id}/activate`); toast.success("Campagna attivata"); loadPeriods(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removePeriod = async (id) => {
    try { await api.delete(`/admin/periods/${id}`); toast.success("Campagna eliminata"); setSelected(null); loadPeriods(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createPromo = async () => {
    try {
      await api.post("/admin/promotions", { period_id: selected, name: prForm.name, prize: prForm.prize, target: parseInt(prForm.target) || 0, value_per_sale: parseFloat(prForm.value_per_sale) || 0, type: prForm.type });
      toast.success("Promozione creata"); setPromoOpen(false); setPrForm({ name: "", prize: "", target: "", value_per_sale: "", type: "vendite" }); loadPromos(selected);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removePromo = async (id) => {
    try { await api.delete(`/admin/promotions/${id}`); toast.success("Promozione eliminata"); loadPromos(selected); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditPeriod = (p) => { setEditPeriod(p); setEpForm({ name: p.name, start_date: p.start_date, end_date: p.end_date }); };
  const savePeriodEdit = async () => {
    try { await api.patch(`/admin/periods/${editPeriod.id}`, epForm); toast.success("Campagna aggiornata"); setEditPeriod(null); loadPeriods(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEditPromo = (p) => { setEditPromo(p); setEprForm({ name: p.name, prize: p.prize, target: String(p.target), value_per_sale: String(p.value_per_sale), type: p.type || "vendite" }); };
  const savePromoEdit = async () => {
    try {
      await api.patch(`/admin/promotions/${editPromo.id}`, { name: eprForm.name, prize: eprForm.prize, target: parseInt(eprForm.target) || 0, value_per_sale: parseFloat(eprForm.value_per_sale) || 0, type: eprForm.type });
      toast.success("Promozione aggiornata"); setEditPromo(null); loadPromos(selected);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!periods) return <Skeleton className="h-64 bg-[#FFFFFF]" />;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="campaigns-tab">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold flex items-center gap-2"><CalendarRange className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Campagne</h3>
          <Button size="sm" onClick={() => setPeriodOpen(true)} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="create-period-btn"><Plus className="w-4 h-4" strokeWidth={2} /></Button>
        </div>
        {periods.map((p) => (
          <Card key={p.id} onClick={() => setSelected(p.id)} className={`p-4 bg-[#FFFFFF] cursor-pointer transition-colors ${selected === p.id ? "border-[#0B6BCB]" : "border-[#DCE7F5] hover:border-white/20"}`} data-testid={`period-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-[#5A6B85] mt-0.5">{formatDate(p.start_date)} – {formatDate(p.end_date)}</div>
              </div>
              {p.is_active && <Badge className="bg-[#0B6BCB]/15 text-[#0B6BCB] border-[#0B6BCB]/30 text-xs">Attiva</Badge>}
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="ghost" className="h-7 text-xs text-[#5A6B85] hover:text-[#0B6BCB]" onClick={(e) => { e.stopPropagation(); openEditPeriod(p); }} data-testid={`edit-period-${p.id}`}><Pencil className="w-3 h-3 mr-1" /> Modifica</Button>
              {!p.is_active && <Button size="sm" variant="ghost" className="h-7 text-xs text-[#5A6B85] hover:text-[#0B6BCB]" onClick={(e) => { e.stopPropagation(); activate(p.id); }} data-testid={`activate-period-${p.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Attiva</Button>}
              <Button size="sm" variant="ghost" className="h-7 text-xs text-[#5A6B85] hover:text-red-600" onClick={(e) => { e.stopPropagation(); removePeriod(p.id); }} data-testid={`delete-period-${p.id}`}><Trash2 className="w-3 h-3 mr-1" /> Elimina</Button>
            </div>
          </Card>
        ))}
        {periods.length === 0 && <p className="text-[#5A6B85] text-sm">Nessuna campagna.</p>}
      </div>

      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold">Promozioni</h3>
          <Button size="sm" disabled={!selected} onClick={() => setPromoOpen(true)} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold disabled:opacity-40" data-testid="create-promo-btn"><Plus className="w-4 h-4 mr-1" strokeWidth={2} /> Nuova promozione</Button>
        </div>
        <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                <TableHead className="text-[#5A6B85]">Promozione</TableHead>
                <TableHead className="text-[#5A6B85]">Premio</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Soglia</TableHead>
                <TableHead className="text-[#5A6B85]">Tipo</TableHead>
                <TableHead className="text-[#5A6B85] text-right">€/unità</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promos.map((p) => (
                <TableRow key={p.id} className="border-[#DCE7F5]" data-testid={`promo-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-[#5A6B85]">{p.prize}</TableCell>
                  <TableCell><Badge className="bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5]">{p.type === "pezzi" ? "Pezzi" : "Vendite"}</Badge></TableCell>
                  <TableCell className="text-right text-[#5A6B85]">{p.target}</TableCell>
                  <TableCell className="text-right text-[#0B6BCB]">{eur(p.value_per_sale)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-[#0B6BCB]" onClick={() => openEditPromo(p)} data-testid={`edit-promo-${p.id}`}><Pencil className="w-4 h-4" strokeWidth={1.5} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-red-600" onClick={() => removePromo(p.id)} data-testid={`delete-promo-${p.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {promos.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#5A6B85] py-8">Nessuna promozione per questa campagna</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={periodOpen} onOpenChange={setPeriodOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="create-period-dialog">
          <DialogHeader><DialogTitle className="font-display">Nuova campagna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome" value={pForm.name} onChange={(v) => setPForm({ ...pForm, name: v })} testid="period-name-input" />
            <Field label="Data inizio" type="date" value={pForm.start_date} onChange={(v) => setPForm({ ...pForm, start_date: v })} testid="period-start-input" />
            <Field label="Data fine" type="date" value={pForm.end_date} onChange={(v) => setPForm({ ...pForm, end_date: v })} testid="period-end-input" />
          </div>
          <DialogFooter><Button onClick={createPeriod} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-period-btn">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="create-promo-dialog">
          <DialogHeader><DialogTitle className="font-display">Nuova promozione</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome promozione" value={prForm.name} onChange={(v) => setPrForm({ ...prForm, name: v })} testid="promo-name-input" />
            <Field label="Nome premio" value={prForm.prize} onChange={(v) => setPrForm({ ...prForm, prize: v })} testid="promo-prize-input" />
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-[#33435E]">Tipo premio</span>
              <select value={prForm.type} onChange={(e) => setPrForm({ ...prForm, type: e.target.value })} className="w-full bg-[#F5F9FF] border border-[#DCE7F5] rounded-md h-10 px-2 text-[#0F1E33]" data-testid="promo-type-select">
                <option value="vendite">A vendite (conta le vendite)</option>
                <option value="pezzi">A pezzi (conta i pezzi / quantità)</option>
              </select>
            </div>
            <Field label={prForm.type === "pezzi" ? "Soglia minima (pezzi)" : "Soglia minima (vendite)"} type="number" value={prForm.target} onChange={(v) => setPrForm({ ...prForm, target: v })} testid="promo-target-input" />
            <Field label={prForm.type === "pezzi" ? "Valore per pezzo (€)" : "Valore per vendita (€)"} type="number" value={prForm.value_per_sale} onChange={(v) => setPrForm({ ...prForm, value_per_sale: v })} testid="promo-value-input" />
          </div>
          <DialogFooter><Button onClick={createPromo} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-promo-btn">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPeriod} onOpenChange={(o) => !o && setEditPeriod(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="edit-period-dialog">
          <DialogHeader><DialogTitle className="font-display">Modifica campagna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome" value={epForm.name} onChange={(v) => setEpForm({ ...epForm, name: v })} testid="edit-period-name-input" />
            <Field label="Data inizio" type="date" value={epForm.start_date} onChange={(v) => setEpForm({ ...epForm, start_date: v })} testid="edit-period-start-input" />
            <Field label="Data fine" type="date" value={epForm.end_date} onChange={(v) => setEpForm({ ...epForm, end_date: v })} testid="edit-period-end-input" />
          </div>
          <DialogFooter><Button onClick={savePeriodEdit} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-edit-period-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPromo} onOpenChange={(o) => !o && setEditPromo(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="edit-promo-dialog">
          <DialogHeader><DialogTitle className="font-display">Modifica promozione</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome promozione" value={eprForm.name} onChange={(v) => setEprForm({ ...eprForm, name: v })} testid="edit-promo-name-input" />
            <Field label="Nome premio" value={eprForm.prize} onChange={(v) => setEprForm({ ...eprForm, prize: v })} testid="edit-promo-prize-input" />
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-[#33435E]">Tipo premio</span>
              <select value={eprForm.type} onChange={(e) => setEprForm({ ...eprForm, type: e.target.value })} className="w-full bg-[#F5F9FF] border border-[#DCE7F5] rounded-md h-10 px-2 text-[#0F1E33]" data-testid="edit-promo-type-select">
                <option value="vendite">A vendite (conta le vendite)</option>
                <option value="pezzi">A pezzi (conta i pezzi / quantità)</option>
              </select>
            </div>
            <Field label={eprForm.type === "pezzi" ? "Soglia minima (pezzi)" : "Soglia minima (vendite)"} type="number" value={eprForm.target} onChange={(v) => setEprForm({ ...eprForm, target: v })} testid="edit-promo-target-input" />
            <Field label={eprForm.type === "pezzi" ? "Valore per pezzo (€)" : "Valore per vendita (€)"} type="number" value={eprForm.value_per_sale} onChange={(v) => setEprForm({ ...eprForm, value_per_sale: v })} testid="edit-promo-value-input" />
          </div>
          <DialogFooter><Button onClick={savePromoEdit} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-edit-promo-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
